import CurrencyInput from 'react-currency-input-field'
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import { Button, DatePicker, Input, Select, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { TransactionTypes } from '@/features/transactions/constants'
import { CardIcon } from './cardParts'
import { WidgetCard } from './WidgetCard'
import type {
  DateBounds,
  ManualTransactionRow,
  ManualTransactionType,
} from './manualTransactions'

interface ManualTransactionsCardProps {
  rows: ManualTransactionRow[]
  bounds: DateBounds
  canSave: boolean
  saving: boolean
  locked: boolean
  saveError: string | null
  onChange: (index: number, row: ManualTransactionRow) => void
  onAddRow: () => void
  onSave: () => void
}

const TYPE_OPTIONS = [
  { value: TransactionTypes.EXPENSE, text: 'Expense' },
  { value: TransactionTypes.INCOME, text: 'Income' },
] satisfies { value: ManualTransactionType; text: string }[]

const COUNTERPARTY_LABEL: Record<ManualTransactionType, string> = {
  [TransactionTypes.EXPENSE]: 'Paid to',
  [TransactionTypes.INCOME]: 'Received from',
}

const FIELD =
  'h-10 w-full rounded-lg border border-neutral-200 bg-transparent px-4 py-2 text-base shadow-sm placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 md:text-sm body-sm'

export const ManualTransactionsCard = ({
  rows,
  bounds,
  canSave,
  saving,
  locked,
  saveError,
  onChange,
  onAddRow,
  onSave,
}: ManualTransactionsCardProps) => (
  <WidgetCard>
    <div className="flex items-start gap-3">
      <CardIcon icon={faPenToSquare} />
      <div className="flex flex-col">
        <Text variant={TypographyStyles.LABEL_MD}>
          Add your practice transactions
        </Text>
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {"I'll add these to your books like any other"}
        </Text>
      </div>
    </div>
    <ul className="@container flex flex-col">
      {rows.map((row, index) => (
        <li
          // Rows are only ever appended, so the index is stable.
          key={index}
          className="grid grid-cols-1 gap-3 border-t border-neutral-100 py-4 first:border-t-0 first:pt-0 @md:grid-cols-2 @2xl:grid-cols-[2fr_2fr_3fr_2fr]"
        >
          <Select<ManualTransactionType>
            value={row.type}
            options={TYPE_OPTIONS}
            disabled={locked}
            onValueChange={(type) => onChange(index, { ...row, type })}
          >
            <Select.Trigger aria-label={`Type, row ${index + 1}`}>
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {TYPE_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.text}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          <DatePicker
            aria-label={`Date, row ${index + 1}`}
            placeholder="mm/dd/yyyy"
            inputClassName="body-sm"
            value={row.date}
            minDate={bounds.earliest.toJSDate()}
            maxDate={bounds.latest.toJSDate()}
            disabled={locked}
            onChange={(date: string) => onChange(index, { ...row, date })}
          />
          <Input
            aria-label={`${COUNTERPARTY_LABEL[row.type]}, row ${index + 1}`}
            placeholder={COUNTERPARTY_LABEL[row.type]}
            inputClassName="body-sm"
            value={row.description}
            disabled={locked}
            onChange={(event) =>
              onChange(index, { ...row, description: event.target.value })
            }
          />
          <CurrencyInput
            aria-label={`Amount, row ${index + 1}`}
            className={FIELD}
            prefix="$"
            placeholder="$0.00"
            decimalsLimit={2}
            allowNegativeValue={false}
            value={row.amount}
            disabled={locked}
            onValueChange={(value) =>
              onChange(index, { ...row, amount: value ?? '' })
            }
          />
        </li>
      ))}
    </ul>
    <Button
      size="sm"
      variant="ghost"
      className="w-fit text-neutral-900 underline"
      disabled={locked}
      onClick={onAddRow}
    >
      + Add another
    </Button>
    {saveError && (
      <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
        {saveError}
      </Text>
    )}
    <Button
      size="sm"
      variant="primary"
      className="w-fit"
      disabled={!canSave || saving}
      loading={saving}
      onClick={onSave}
    >
      Save
    </Button>
  </WidgetCard>
)
